import React, { useEffect, useRef, useState } from 'react';

import { buscarNota } from '../../../api/previsaoPecas/previsaoPecasApi';
import { DetalheDaNota } from '../../../interfaces/PrevisaoPecas';
import { formatarDiaPuro, formatarQuantidade } from '../formatos';
import '../../../components/modal/add-car/AddCarModal.css'; // overlay compartilhado
import '../../../shared/ui/dashboard-base.css'; // casco `do-modal-*`
import '../../portal_cotacoes/components/ConviteAcoesModal.css'; // seções e grade rótulo/valor
import '../../../shared/ui/badge-status.css';
import '../../../shared/ui/modal-close-button.css';
import '../../../shared/feedback/erro-de-tela.css';

interface Props {
  notaId: string;
  onFechar: () => void;
}

const DINHEIRO = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Uma nota fiscal de entrada (previsao-pecas/at-02): o que ela trouxe e em qual peça de qual pedido
 * cada item caiu. É a prova por trás de "SOAC, pela nota N".
 *
 * 🔴 Falha de carga nunca vira "a nota não trouxe nada"; nota com itens ainda não lidos do Nasajon
 * diz isso, em vez de mostrar lista vazia.
 */
const NotaModal: React.FC<Props> = ({ notaId, onFechar }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [nota, setNota] = useState<DetalheDaNota | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let ativo = true;
    buscarNota(notaId)
      .then((n) => {
        if (ativo) setNota(n);
      })
      .catch(() => {
        // Sem toast: o aviso fica DENTRO do modal, no lugar do conteúdo.
        if (ativo) setFalhou(true);
      });
    return () => {
      ativo = false;
    };
  }, [notaId]);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(evento.target as Node)) onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar]);

  const titulo = `Nota ${nota?.numero ?? ''}`.trim();

  return (
    <div className="modal-overlay">
      <div className="do-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Nota de entrada">
        <div className="do-modal-header">
          <h2 className="do-modal-titulo">{titulo}</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="do-modal-corpo">
          {falhou && (
            <div className="erro-de-tela" role="alert">
              Não foi possível carregar a nota. Isso não quer dizer que ela não trouxe nada. Tente de novo; se persistir,
              acione a TI.
            </div>
          )}
          {!falhou && !nota && <p className="convite-modal-vazio">Carregando...</p>}

          {nota && (
            <>
              <section className="convite-modal-secao">
                <h3>Nota de entrada</h3>
                <div className="convite-modal-grade">
                  <span className="convite-modal-rotulo">Fornecedor</span>
                  <span>{nota.fornecedorNome ?? 'não identificado'}</span>
                  <span className="convite-modal-rotulo">Entrada no Nasajon</span>
                  <span>{formatarDiaPuro(nota.dataEntrada, 'data ainda não lida')}</span>
                  <span className="convite-modal-rotulo">Pedidos ligados</span>
                  <span>{nota.pedidos.map((p) => p.numero ?? 'sem número').join(', ')}</span>
                </div>
              </section>

              <section className="convite-modal-secao">
                <h3>Itens da nota</h3>
                {!nota.itensLidos && (
                  <p className="convite-modal-vazio">
                    Os itens desta nota ainda não foram lidos do Nasajon. A sincronização de entregas os traz.
                  </p>
                )}
                {nota.itensLidos && nota.itens.length === 0 && (
                  <p className="convite-modal-vazio">O Nasajon não informou itens nesta nota.</p>
                )}
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                  {nota.itens.map((i) => (
                    <li key={i.id} style={{ fontSize: 13, borderBottom: '1px solid var(--tema-linha, #eee)', paddingBottom: 8 }}>
                      <div>
                        <strong>
                          {formatarQuantidade(i.quantidade ?? 0)} {i.unidade ?? ''} {i.descricao ?? 'item sem descrição'}
                        </strong>
                        {i.codigo ? <span className="convite-modal-rotulo"> ({i.codigo})</span> : null}
                        {i.valorUnitario != null && (
                          <span className="convite-modal-rotulo"> · {DINHEIRO(i.valorUnitario)} cada</span>
                        )}
                      </div>
                      {i.destinos.length === 0 ? (
                        <span className="cilia-badge badge-laranja">A identificar</span>
                      ) : (
                        i.destinos.map((d) => (
                          <div key={d.chegadaId} className="convite-modal-rotulo" style={d.cancelada ? { textDecoration: 'line-through' } : undefined}>
                            {formatarQuantidade(d.quantidade)} na peça {d.pecaDescricao ?? 'sem descrição'} do pedido{' '}
                            {d.pedidoNumero ?? 'sem número'}
                            {d.distribuicao === 'EXCEDENTE' ? ' (acima do pedido)' : ''}
                            {d.distribuicao === 'POR_SALDO' ? ' (dividida pelo saldo entre pedidos)' : ''}
                            {d.registradoPor !== 'SOAC' ? ` · identificada por ${d.registradoPor}` : ''}
                            {d.cancelada ? ' · cancelada' : ''}
                          </div>
                        ))
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotaModal;
